import React from "react";
import { graphql, useLazyLoadQuery, useQueryLoader } from "react-relay";
import { useNavigate, useParams } from "react-router-dom";
import { Typography, styled, Tab, Tabs } from "@mui/material";

import { FileDetailsQuery } from "./__generated__/FileDetailsQuery.graphql";
import ErrorBoundary from "../../components/common/ErrorBoundary";
import SimpleBackdrop from "../../components/common/SimpleBackdrop";
import PageContainer from "../../components/common/PageContainer";
import TabContainer from "../../components/common/TabContainer";
import TabPanel from "../../components/common/TabPanel";
import FileDetailsTab from "./FileDetailsTab";
import RuptureSetReportTab from "../InversionSolution/RuptureSetReportTab";
import PredecessorsTab, { hasPredecessors, predecessorsTabQuery } from "../../components/common/PredecessorsTab";
import { PredecessorsTabQuery } from "../../components/common/__generated__/PredecessorsTabQuery.graphql";
import CentredCircularProgress from "../../components/common/CentredCircularProgress";

export type FileDetailsParams = {
  id: string;
  tab: string;
};

const StyledTab = styled(Tab)(({ theme }) => ({
  maxWidth: "5vw",
  borderRight: `2px solid ${theme.palette.divider}`,
}));

const Container = styled("div")(() => ({
  flexGrow: 1,
  padding: 20,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
}));

const FileDetails: React.FC = () => {
  const { id, tab } = useParams<FileDetailsParams>();
  const validatedId: string = id ?? "";
  const data = useLazyLoadQuery<FileDetailsQuery>(fileQuery, { id: validatedId });
  const navigate = useNavigate();
  const [predQueryRef, loadPredQuery] = useQueryLoader<PredecessorsTabQuery>(predecessorsTabQuery);

  React.useEffect(() => {
    if (!id) {
      return;
    }
    switch (tab) {
      case "PredecessorsTab":
        loadPredQuery({ id }, { fetchPolicy: "network-only" });
        break;
    }
  }, [id, loadPredQuery, tab]);

  if (!data?.node?.file_name) {
    return (
      <Container>
        <Typography variant="h5" gutterBottom>
          File ID Not Found
        </Typography>
      </Container>
    );
  }

  const renderTab = () => {
    switch (tab) {
      case "RuptureSetReportTab":
        return <TabPanel>{id && data && <RuptureSetReportTab fileId={id} metaAsString="" />}</TabPanel>;
      case "PredecessorsTab":
        return (
          <TabPanel>
            <React.Suspense fallback={<CentredCircularProgress />}>
              {predQueryRef && <PredecessorsTab queryRef={predQueryRef} />}
            </React.Suspense>
          </TabPanel>
        );
      default:
        return <TabPanel>{data && <FileDetailsTab data={data} />}</TabPanel>;
    }
  };

  const metaKeys = data?.node?.meta?.map((kv) => kv?.k ?? "");
  const legacyRuptureSet =
    ["fault_model", "scaling_relationship"].every((k) => metaKeys?.includes(k)) &&
    data?.node?.__typename !== "RuptureSet";
  const hasRuptureSet = legacyRuptureSet || data?.node?.__typename === "RuptureSet";
  const fileType = legacyRuptureSet ? "Rupture Set (implied)" : data?.node?.__typename;

  return (
    <PageContainer maxWidth="xl">
      <Typography variant="h5" gutterBottom>
        {fileType}: {id}&nbsp;
      </Typography>
      <TabContainer maxWidth="xl">
        <Tabs
          orientation="vertical"
          value={tab ?? "DetailsTab"}
          onChange={(_e, val) => {
            navigate(`/File/${id}/${val}`);
          }}
        >
          <StyledTab label="Details" id="DetailsTab" value={"DetailsTab"} />
          {hasRuptureSet && <StyledTab label="Rupture Diags" id="RuptureSetReportTab" value="RuptureSetReportTab" />}
          {hasPredecessors(data.node?.__typename) && (
            <StyledTab label="Parents" id="PredecessorsTab" value="PredecessorsTab" />
          )}
        </Tabs>
        {renderTab()}
      </TabContainer>
    </PageContainer>
  );
};

export const FileDetailsPage: React.FC = () => {
  return (
    <ErrorBoundary>
      <React.Suspense fallback={<SimpleBackdrop />}>
        <FileDetails />
      </React.Suspense>
    </ErrorBoundary>
  );
};

export const fileQuery = graphql`
  query FileDetailsQuery($id: ID!) {
    node(id: $id) {
      __typename
      id
      ... on FileInterface {
        created
        file_name
        file_size
        file_url
        md5_digest
        meta {
          k
          v
        }
      }
      ... on RuptureSet {
        fault_models
        produced_by {
          __typename
          ... on Node {
            id
          }
        }
        metrics {
          k
          v
        }
      }
      ... on InversionSolutionNrml {
        source_solution {
          ... on AggregateInversionSolution {
            id
          }
          ... on InversionSolution {
            id
          }
          ... on ScaledInversionSolution {
            id
          }
          ... on TimeDependentInversionSolution {
            id
          }
        }
      }
    }
  }
`;

export default FileDetailsPage;
